import { flowRight, isString, get, inRange, noop } from 'lodash';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from '../../../common/components/runtime-context';
import {
  RichContentViewer as Viewer,
  linkTypeMapper,
  imageTypeMapper,
  videoTypeMapper,
  dividerTypeMapper,
  htmlTypeMapper,
  HTML_TYPE,
  IMAGE_TYPE,
  HashTag,
  HashTagStrategy,
  LinkParseStrategy,
  LinkViewer,
  MENTION_TYPE,
  mentionsTypeMapper,
  CodeBlockDecorator,
} from '@wix/communities-forum-rce/dist/src/viewer';

import withDeviceType from '../../hoc/with-device-type';
import { getAppSettings } from '../../../common/store/app-settings/app-settings-selectors';
import { getIsSocialSharingLinksEnabled } from '../../selectors/app-settings-selectors';
import getOuterUrl from '../../services/get-outer-url';
import { getLocation } from '../../../common/store/location/location-selectors';
import { isSeo, isEditor } from '../../../common/store/basic-params/basic-params-selectors';
import getImageUrl from '../../services/get-image-url';
import theme from '../rich-content-editor/theme';
import styles from './rich-content-viewer.scss';
import {
  createHashtagHref,
  createHashtagPath,
  MAX_PINTEREST_IMAGE_SIZE,
  HTML_IFRAME_SRC,
} from '../rich-content-editor/utils';
import withFontClassName from '../../hoc/with-font-class-name';
import { isMemberAreaInstalled } from '../../../common/store/communities-context/communities-context-selectors';

const relValue = 'noreferrer';
const anchorTarget = '_blank';

class RichContentViewer extends Component {
  constructor(props) {
    super(props);
    this.initViewerProps();
  }

  shouldComponentUpdate(nextProps) {
    return this.props.isEditor || nextProps.pageUrl !== this.props.pageUrl;
  }

  componentDidUpdate() {
    if (this.props.isEditor) {
      this.initTheme();
    }
  }

  getImagePluginConfig() {
    const { isSocialSharingLinksEnabled } = this.props;
    const showPin = this.props.readOnly && isSocialSharingLinksEnabled;
    return {
      imageProps: showPin
        ? src => ({
            'data-pin-url': this.props.pageUrl,
            'data-pin-media': isString(src)
              ? src
              : getImageUrl(src, MAX_PINTEREST_IMAGE_SIZE, MAX_PINTEREST_IMAGE_SIZE),
          })
        : { 'data-pin-nopin': true },
    };
  }

  onHashTagClick = event => {
    event.preventDefault();
    event.stopPropagation();
    const pathname = createHashtagPath(event.target.innerText);
    this.props.navigateWithinForum(pathname);
  };

  initViewerProps() {
    const { isMembersAreaInstalled, navigateToProfile } = this.props;
    this.typeMappers = [
      videoTypeMapper,
      imageTypeMapper,
      dividerTypeMapper,
      htmlTypeMapper,
      linkTypeMapper,
      mentionsTypeMapper,
    ];

    this.helpers = {};
    this.initTheme();

    this.config = {
      [HTML_TYPE]: {
        htmlIframeSrc: HTML_IFRAME_SRC,
      },
      [IMAGE_TYPE]: this.getImagePluginConfig(),
      [MENTION_TYPE]: {
        onMentionClick: isMembersAreaInstalled ? mention => navigateToProfile(mention.slug) : null,
        getMentionLink: noop,
      },
    };

    this.initDecorators();
  }

  initTheme() {
    const {
      appSettings,
      titleFontClassName,
      contentFontClassName,
      compact,
      isSeo,
      themeGetter,
    } = this.props;

    this.theme = themeGetter({
      appSettings,
      readOnly: true,
      compact,
      isViewer: true,
      isSeo,
      titleFontClassName,
      contentFontClassName,
    });
  }

  initDecorators() {
    const { initialRawState, sectionUrl } = this.props;

    const hasLinkEntity = ({ entityRanges }, start) =>
      entityRanges.some(
        ({ key, offset, length }) =>
          get(initialRawState, `entityMap.${key}.type`) === 'LINK' &&
          inRange(start, offset, offset + length),
      );

    this.decorators = [
      {
        strategy: (block, callback) => {
          HashTagStrategy(block, (...args) =>
            !hasLinkEntity(block, ...args) ? callback(...args) : [],
          ); // eslint-disable-line new-cap
        },
        component: ({ children, decoratedText }) =>
          children.map((child, i) => (
            <HashTag
              key={i}
              decoratedText={decoratedText}
              theme={this.theme}
              onClick={this.onHashTagClick}
              createHref={createHashtagHref(sectionUrl)}
            >
              {child}
            </HashTag>
          )),
      },
      {
        strategy: (block, callback) => {
          LinkParseStrategy(block, (...args) =>
            !hasLinkEntity(block, ...args) ? callback(...args) : [],
          ); // eslint-disable-line new-cap
        },
        component: ({ children, decoratedText, rel, target }) =>
          children.map((child, i) => (
            <LinkViewer
              key={i}
              theme={this.theme}
              componentData={{ rel, target, url: decoratedText }}
              anchorTarget={anchorTarget}
              relValue={relValue}
            >
              {child}
            </LinkViewer>
          )),
      },
      new CodeBlockDecorator({ theme: this.theme }),
    ];
  }

  render() {
    const { initialRawState, isMobile } = this.props;

    return (
      <div className={styles.container}>
        <Viewer
          typeMappers={this.typeMappers}
          ref={ref => (this.editor = ref)}
          {...this.props}
          initialState={initialRawState}
          helpers={this.helpers}
          decorators={this.decorators}
          theme={this.theme}
          platform={isMobile ? 'mobile' : 'desktop'}
          anchorTarget={anchorTarget}
          relValue={relValue}
          config={this.config}
        />
      </div>
    );
  }
}

RichContentViewer.propTypes = {
  initialRawState: PropTypes.object,
  compact: PropTypes.bool,
  appSettings: PropTypes.object.isRequired,
  navigateWithinForum: PropTypes.func.isRequired,
  pageUrl: PropTypes.string.isRequired,
  isMobile: PropTypes.bool.isRequired,
  titleFontClassName: PropTypes.string,
  contentFontClassName: PropTypes.string,
  isSeo: PropTypes.bool,
  themeGetter: PropTypes.func,
  navigateToProfile: PropTypes.func,
  isMembersAreaInstalled: PropTypes.bool,
  isEditor: PropTypes.bool,
  isSocialSharingLinksEnabled: PropTypes.bool,
  sectionUrl: PropTypes.string,
};

RichContentViewer.defaultProps = {
  compact: false,
  themeGetter: theme,
};

const mapRuntimeToProps = (state, ownProps, actions) => {
  const location = getLocation(state);
  const sectionUrl = location.sectionUrl;
  return {
    appSettings: getAppSettings(state),
    pageUrl: getOuterUrl(location.pathname, sectionUrl),
    isSeo: isSeo(state),
    isEditor: isEditor(state),
    isMembersAreaInstalled: isMemberAreaInstalled(state),
    isSocialSharingLinksEnabled: getIsSocialSharingLinksEnabled(state),
    sectionUrl,
    navigateWithinForum: actions.navigateWithinForum,
    navigateToProfile: actions.navigateToProfile,
  };
};

export default flowRight(
  connect(mapRuntimeToProps),
  withDeviceType,
  withFontClassName,
)(RichContentViewer);
